<template>
  <app-layout header-type="landing">
    <template #header>
      <div>
        <h1 class="text-3xl md:text-4xl font-light">
          Your profile
        </h1>
      </div>
    </template>
    <div class="container flex flex-wrap mt-12">
      <div class="w-full md:w-3/5">
        <div
          v-if="message"
          class="bg-positive-100 py-2 px-4 mb-6 text-sm text-positive-900 border border-positive-500 rounded"
        >
          <p>
            <icon
              icon="check"
              class="w-5 h-5 mr-2 icons-positive align-middle"
            />
            Your profile has been successfully updated.
          </p>
        </div>
        <form class="profile" @submit.prevent="update">
          <div class="p-6 bg-white shadow-lg">
            <auth-input
              v-model="auth.name"
              label="Name"
              input-type="text"
              :error="getError('name')"
              :required="true"
              placeholder="Your Name"
              :loading="loading"
              :autocomplete="true"
            />

            <auth-input
              v-model="auth.email"
              label="Email"
              input-type="email"
              :error="getError('email')"
              :required="true"
              placeholder="e.g. your.name@email.com"
              :loading="loading"
              :autocomplete="true"
              :locked="true"
            />

            <auth-input
              v-model="auth.job_title"
              label="Job Title"
              input-type="text"
              :error="getError('email')"
              :required="false"
              placeholder="e.g. Managing Director"
              :loading="loading"
              :autocomplete="true"
            />

            <auth-input
              v-model="auth.telephone"
              label="Telephone"
              input-type="tel"
              :error="getError('telephone')"
              :required="false"
              placeholder=""
              :loading="loading"
              :autocomplete="true"
            />

            <div class="pt-2 pb-6">
              <div class="text-center border-b border-grey-200">
                <p class="font-semibold -mb-3">
                  <span class="bg-white px-3">Authentication</span>
                </p>
              </div>
            </div>

            <auth-input
              v-model="auth.password"
              label="New Password"
              input-type="password"
              :error="getError('password')"
              :required="false"
              placeholder="Your Password"
              :loading="loading"
              :autocomplete="true"
            />

            <auth-input
              v-model="auth.password_confirmation"
              label="Repeat Password"
              input-type="password"
              :loading="loading"
            />

            <div class="pt-2 pb-6">
              <div class="text-center border-b border-grey-200">
                <p class="font-semibold -mb-3">
                  <span class="bg-white px-3">Company Details</span>
                </p>
              </div>
            </div>

            <auth-input
              v-model="auth.company_number"
              label="Company Number"
              input-type="text"
              :error="getError('company_number')"
              placeholder="01234567"
              :maxlength="8"
              :loading="loading"
              :autocomplete="true"
              :disabled="!!state.user.company.number"
              :locked="!!state.user.company.number"
              @blur="companyPrefixZero"
            >
              <div
                v-if="hasCompany && !state.user.company.number"
                class="bg-grey-100 -ml-2 -mr-2 -mb-2 mt-2 px-3 py-2 text-black font-light"
              >
                <h2 class="text-xs text-grey-500">
                  Your company's details
                </h2>
                <p class="text-sm">
                  {{ company_details.name | sentencecase }}
                </p>
                <p class="text-xs">
                  {{ companyAddress }}
                </p>
              </div>
            </auth-input>

            <transition
              enter-active-class="transition-all ease-in duration-300"
              enter-class="max-h-0 opacity-0 bg-white"
              enter-to-class="max-h-screen opacity-100"
              leave-active-class="transition-all ease-out duration-200"
              leave-class="max-h-screen opacity-100"
              leave-to-class="max-h-0 bg-white opacity-0"
              mode="out-in"
            >
              <div
                v-if="state.user.company.number || !auth.company_number.length"
                class="border-t"
              >
                <auth-input
                  v-model="auth.company_name"
                  class="mb-4 relative"
                  label="Company Name"
                  :error="getError('company_name')"
                  required
                  input-type="text"
                  :loading="loading"
                  :disabled="!!state.user.company.number"
                  :locked="!!state.user.company.number"
                />
                <auth-input
                  v-model="auth.address_line_1"
                  class="mb-2"
                  label="Address"
                  :error="getError('address_line_1')"
                  required
                  input-type="text"
                  :loading="loading"
                  :disabled="!!state.user.company.number"
                  :locked="!!state.user.company.number"
                />
                <auth-input
                  v-model="auth.address_line_2"
                  class="mb-4"
                  :error="getError('address_line_2')"
                  input-type="text"
                  :loading="loading"
                  :disabled="!!state.user.company.number"
                  :locked="!!state.user.company.number"
                />
                <auth-input
                  v-model="auth.town"
                  label="Town"
                  class="mb-4"
                  :error="getError('town')"
                  input-type="text"
                  :loading="loading"
                  :disabled="!!state.user.company.number"
                  :locked="!!state.user.company.number"
                />
                <auth-input
                  v-model="auth.county"
                  label="County"
                  class="mb-4"
                  :error="getError('county')"
                  required
                  input-type="text"
                  :loading="loading"
                  :disabled="!!state.user.company.number"
                  :locked="!!state.user.company.number"
                />
                <auth-input
                  v-model="auth.postcode"
                  label="Post Code"
                  class="mb-4"
                  :error="getError('postcode')"
                  required
                  input-type="text"
                  :loading="loading"
                  :disabled="!!state.user.company.number"
                  :locked="!!state.user.company.number"
                />
              </div>
            </transition>
          </div>

          <div class="w-full md:w-48">
            <app-button
              type="submit"
              colour="primary"
              class="mt-8 mb-4"
              :disabled="loading"
            >
              <span v-if="!loading">Update</span>
              <span v-else>Updating...</span>
            </app-button>
          </div>
        </form>
      </div>

      <div class="w-full md:w-2/5 md:pl-12">
        <div
          class="sticky p-6 content"
          style="top: 6rem"
        >
          <h3 class="flex items-center mb-4">
            <icon icon="important" class="w-8 h-8 mr-2 icons-primary" />
            Locked Information
          </h3>
          <p>
            If your company details have changed (via acquisition/buyout),
            please contact a member of the APC team to update your email and
            company details.
          </p>
          <p>
            If you have moved company, please set up a new account and requst
            the deletion of this account.
          </p>
          <div class="w-12 h-1 bg-primary-500 mb-6"></div>
          <icon-link
            icon="cheveron-right-circle"
            icon-placement="right"
            :href="route('help.index')"
          >
            Visit Help Page
          </icon-link>
        </div>
      </div>
    </div>
  </app-layout>
</template>

<script>
import debounce from "lodash/debounce";
import pick from "lodash/pick";
import { requestMixin } from "../mixins/request";
export default {
  name: "Profile",
  mixins: [requestMixin],
  data() {
    return {
      auth: {
        name: this.state.user.name || "",
        email: this.state.user.email,
        telephone: this.state.user.telephone || "",
        job_title: this.state.user.job_title || "",
        company_number: this.state.user.company.number || "",
        company_name: this.state.user.company.name || "",
        address_line_1: this.state.user.company.address_line_1 || "",
        address_line_2: this.state.user.company.address_line_2 || "",
        town: this.state.user.company.town || "",
        county: this.state.user.company.county || "",
        postcode: this.state.user.company.postcode || "",
        password: "",
        password_confirmation: ""
      },
      company_details: this.state.user.company,
      loading: false,
      message: false
    };
  },
  computed: {
    hasCompany() {
      if (this.auth.unregistered_organisation) return true;
      return (
        Object.keys(this.company_details).length > 1 &&
        this.auth.company_number.length === 8
      );
    },
    companyAddress() {
      let address = pick(this.company_details, [
        "address_line_1",
        "address_line_2",
        "town",
        "county",
        "postcode"
      ]);
      return Object.values(address)
        .filter(Boolean)
        .join(", ");
    }
  },
  watch: {
    "auth.company_number": debounce(function() {
      return this.companySearch();
    }, 500)
  },
  created() {
    document.title = `Your Profile :: ${document.title}`;
  },
  methods: {
    update() {
      this.loading = true;
      let company_fields = [];
      if (!this.state.user.company.number) {
        company_fields = [
          "company_number",
          "company_name",
          "address_line_1",
          "address_line_2",
          "town",
          "county",
          "postcode"
        ];
      }
      return axios
        .post(
          route("profile"),
          pick(this.auth, [
            "name",
            "telephone",
            "job_title",
            "password",
            "password_confirmation",
            ...company_fields
          ])
        )
        .then(response => this.updateReponse(response))
        .catch(error => this.requestError(error))
        .then(() => (this.loading = false));
    },
    updateReponse(response) {
      if (response.data.hasOwnProperty("errors")) {
        const error = { response: { ...response } };
        this.requestError(error);
      } else {
        this.auth.password = "";
        this.auth.password_confirmation = "";
        this.message = true;
      }
      window.scrollTo({
        top: 0,
        smooth: true
      });
    },
    companyPrefixZero() {
      if (this.auth.company_number.length === 7)
        return (this.auth.company_number = 0 + this.auth.company_number + "");
    },
    companySearch() {
      this.removeError("company_number");
      if (this.auth.company_number.length !== 8) return;
      if (this.auth.company_number === this.company_details.dirty) return;
      this.company_details = pick(this.company_details, ["dirty"]);
      this.company_details.dirty = this.auth.company_number;

      return axios
        .post(route("api.company-search"), pick(this.auth, "company_number"))
        .then(response => this.companySearchResponse(response))
        .catch(error => this.requestError(error));
    },
    companySearchResponse(response) {
      return (this.company_details = {
        ...this.company_details,
        ...response.data
      });
    }
  }
};
</script>
<style>
.profile .auth-input label {
  @apply bg-grey-100;
}
</style>
